import { useState } from "react";
import { Wallet, DollarSign, Users, TrendingUp, Calendar } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format, parseISO } from "date-fns";
import { SupplierInvoicesModal } from "@/components/modals/SupplierInvoicesModal";
import { InvoiceGroupedList } from "@/components/invoices/InvoiceGroupedList";

interface Invoice {
  id: string;
  status: string;
  discount_amount: number | null;
  payment_amount: number;
  discounted_payment_amount: number | null;
  payment_due_date: string;
  supplier_name: string;
  supplier_email: string;
  created_at: string;
}

interface KeyMetricsProps {
  invoices: Invoice[];
  onInvoicesUpdated?: () => void;
  availableBudget: number;
  budgetPeriodStart?: string;
  budgetPeriodEnd?: string;
  onBudgetClick?: () => void;
  onPaymentRunClick?: () => void;
  onSavingsClick?: () => void;
  allInvoices?: Invoice[];
  hideSuppliers?: boolean;
}

export function KeyMetrics({
  invoices,
  availableBudget,
  budgetPeriodStart,
  budgetPeriodEnd,
  onBudgetClick,
  onPaymentRunClick,
  onSavingsClick,
  allInvoices,
  hideSuppliers = false,
}: KeyMetricsProps) {
  const [showSuppliersModal, setShowSuppliersModal] = useState(false);
  const [showAcceleratedModal, setShowAcceleratedModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<{ name: string; email: string } | null>(
    null
  );

  // Use allInvoices (real + historical) for supplier stats if provided
  const invoicesForStats = allInvoices || invoices;

  const getBudgetPeriodDisplay = () => {
    if (budgetPeriodStart && budgetPeriodEnd) {
      const start = parseISO(budgetPeriodStart);
      const end = parseISO(budgetPeriodEnd);
      return `${format(start, "MMM d")} - ${format(end, "MMM d")}`;
    }
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return `${format(startOfMonth, "MMM d")} - ${format(endOfMonth, "MMM d")}`;
  };

  const invoicesForACH = invoices.filter(
    (inv) => inv.status === "early_payment_scheduled" || inv.status === "early_payment"
  );
  const dueInNextRun = invoicesForACH.reduce(
    (sum, inv) => sum + (inv.discounted_payment_amount || inv.payment_amount),
    0
  );

  const dueDates = [...new Set(invoicesForACH.map((inv) => inv.payment_due_date))].sort();
  const dueDateDisplay =
    dueDates.length > 0
      ? dueDates.length === 1
        ? format(parseISO(dueDates[0]), "MMM d")
        : `${format(parseISO(dueDates[0]), "MMM d")} - ${format(parseISO(dueDates[dueDates.length - 1]), "MMM d")}`
      : null;

  const acceleratedInvoices = invoicesForStats.filter(
    (inv) =>
      inv.status === "early_payment_scheduled" ||
      inv.status === "early_payment_completed" ||
      inv.status === "early_payment" ||
      inv.status === "paid"
  );
  const totalSaved = acceleratedInvoices.reduce((sum, inv) => sum + (inv.discount_amount || 0), 0);
  const dollarsAccelerated = acceleratedInvoices.reduce(
    (sum, inv) => sum + (inv.discounted_payment_amount || inv.payment_amount),
    0
  );
  const totalOriginalAmount = acceleratedInvoices.reduce((sum, inv) => sum + inv.payment_amount, 0);
  const averageDiscount = totalOriginalAmount > 0 ? (totalSaved / totalOriginalAmount) * 100 : 0;

  const uniqueSuppliers = acceleratedInvoices.filter(
    (inv, idx, arr) => arr.findIndex((i) => i.supplier_email === inv.supplier_email) === idx
  );
  const uniqueSupplierEmails = [...new Set(acceleratedInvoices.map((inv) => inv.supplier_email))];
  const acceptedInvoicesCount = acceleratedInvoices.length;

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);

  const supplierStats = uniqueSuppliers.map((inv) => {
    const supplierInvoices = acceleratedInvoices.filter(
      (i) => i.supplier_email === inv.supplier_email
    );
    return {
      name: inv.supplier_name,
      email: inv.supplier_email,
      invoiceCount: supplierInvoices.length,
      dollarsSaved: supplierInvoices.reduce((sum, i) => sum + (i.discount_amount || 0), 0),
      dollarsAccelerated: supplierInvoices.reduce(
        (sum, i) => sum + (i.discounted_payment_amount || i.payment_amount),
        0
      ),
    };
  });

  const allMetrics = [
    {
      label: "Budget Remaining for Acceleration",
      value: formatCurrency(availableBudget),
      icon: Wallet,
      description: getBudgetPeriodDisplay(),
      accent: "from-amber-500/50 to-amber-400/30",
      onClick: onBudgetClick,
    },
    {
      label: "Dollars Accelerated",
      value: formatCurrency(dollarsAccelerated),
      icon: TrendingUp,
      description: `${acceptedInvoicesCount} invoice(s) · YTD`,
      accent: "from-cyan-500/50 to-cyan-400/30",
      onClick: () => setShowAcceleratedModal(true),
    },
    {
      label: "Due in Next Payment Run",
      value: formatCurrency(dueInNextRun),
      icon: Calendar,
      description: dueDateDisplay
        ? `Due: ${dueDateDisplay}`
        : `${invoicesForACH.length} invoice(s) ready`,
      accent: "from-indigo-500/50 to-indigo-400/30",
      onClick: onPaymentRunClick,
    },
    {
      label: "Saved with DiscoRun",
      value: formatCurrency(totalSaved),
      icon: DollarSign,
      description: `${averageDiscount.toFixed(2)}% avg discount · YTD`,
      accent: "from-emerald-500/50 to-emerald-400/30",
      onClick: onSavingsClick,
    },
    {
      label: "Suppliers Enrolled",
      value: uniqueSupplierEmails.length.toString(),
      icon: Users,
      description: "Accepted early payment",
      accent: "from-cyan-500/50 to-cyan-400/30",
      onClick: () => setShowSuppliersModal(true),
      hidden: hideSuppliers,
    },
  ];

  const metrics = allMetrics.filter((m) => !("hidden" in m && m.hidden));

  return (
    <>
      <div
        className={`grid grid-cols-2 sm:grid-cols-3 ${metrics.length <= 4 ? "lg:grid-cols-4" : "lg:grid-cols-5"} gap-3`}
      >
        {metrics.map((metric) => (
          <div
            key={metric.label}
            className={`relative rounded-xl border border-white/[0.08] bg-[hsl(224_40%_7%)] overflow-hidden p-6 transition-all duration-200 ${metric.onClick ? "cursor-pointer hover:-translate-y-0.5 hover:border-white/[0.14]" : ""}`}
            style={{ boxShadow: "0 0 0 1px rgba(255,255,255,0.05), 0 4px 24px rgba(0,0,0,0.4)" }}
            onClick={metric.onClick}
          >
            {/* Gradient top accent line */}
            <div
              className={`absolute top-0 left-0 right-0 h-px bg-gradient-to-r ${metric.accent}`}
            />

            {/* Decorative icon */}
            <metric.icon className="absolute top-4 right-4 w-8 h-8 opacity-[0.06] text-white" />

            {/* Label */}
            <p className="text-[11px] uppercase tracking-widest text-white/40 font-medium">
              {metric.label}
            </p>

            {/* Value */}
            <p className="text-3xl font-bold text-white tabular-nums font-mono mt-2 truncate">
              {metric.value}
            </p>

            {/* Description */}
            <p className="text-[11px] text-white/40 mt-2 truncate">{metric.description}</p>
          </div>
        ))}
      </div>

      {/* Suppliers Modal */}
      <Dialog open={showSuppliersModal} onOpenChange={setShowSuppliersModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Suppliers Enrolled</DialogTitle>
            <DialogDescription>
              {uniqueSupplierEmails.length} supplier(s) have accepted early payment offers
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-2">
            {supplierStats.map((supplier) => (
              <div
                key={supplier.email}
                className="p-3 bg-muted rounded-lg cursor-pointer hover:bg-muted/70 transition-colors"
                onClick={() => {
                  setSelectedSupplier({ name: supplier.name, email: supplier.email });
                  setShowSuppliersModal(false);
                }}
              >
                <div className="flex justify-between items-start gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground truncate">{supplier.name}</p>
                    <p className="text-sm text-muted-foreground truncate">{supplier.email}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-semibold text-success">
                      {formatCurrency(supplier.dollarsSaved)} saved
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {supplier.invoiceCount} invoice(s)
                    </p>
                  </div>
                </div>
              </div>
            ))}
            {uniqueSupplierEmails.length === 0 && (
              <p className="text-center text-muted-foreground py-4">No suppliers enrolled yet</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Supplier Invoices Drill-down */}
      <SupplierInvoicesModal
        open={!!selectedSupplier}
        onOpenChange={(open) => !open && setSelectedSupplier(null)}
        supplierName={selectedSupplier?.name || ""}
        supplierEmail={selectedSupplier?.email || ""}
        invoices={invoicesForStats}
        onBack={() => {
          setSelectedSupplier(null);
          setShowSuppliersModal(true);
        }}
      />

      {/* Accelerated Invoices Modal */}
      <Dialog open={showAcceleratedModal} onOpenChange={setShowAcceleratedModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Accelerated Invoices</DialogTitle>
            <DialogDescription>{acceptedInvoicesCount} invoice(s)</DialogDescription>
          </DialogHeader>
          <InvoiceGroupedList
            invoices={acceleratedInvoices}
            emptyMessage="No accelerated invoices yet"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
